import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { t } from '../../util/messages';
import { twitterPageURL } from '../../util/urlHelpers';
import config from '../../config/config';
import { ExternalLink, Logo, NamedLink } from '../../components';
import css from './Footer.module.css';

/**
 * Site footer.
 *
 * Four columns — brand, services, company, contact — over a bottom bar.
 *
 * Three columns — brand, company, contact. Services is a single link inside
 * Company, pointing at the hub that lists them all.
 */
/**
 * Glyph-only social marks.
 *
 * The shared `IconSocialMedia*` components render a self-contained badge —
 * their own green ring plus a white glyph — which cannot be placed inside
 * the circular button this design calls for. These are the bare glyphs;
 * colour comes from the button via `currentColor`.
 */
const SOCIAL_GLYPHS = {
  linkedin: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.98 3.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5zM3 9h4v12H3zM10 9h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05C21.4 8.65 22 11.1 22 14.3V21h-4v-6c0-1.43-.03-3.27-2-3.27-2 0-2.3 1.56-2.3 3.17V21h-4z" />
    </svg>
  ),
  facebook: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5h1.65V3.6c-.29-.04-1.27-.13-2.41-.13-2.38 0-4.01 1.46-4.01 4.13v2.3H7.6V13h2.68v8z" />
    </svg>
  ),
  instagram: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" />
    </svg>
  ),
  twitter: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.53 3h3.06l-6.69 7.64L21.75 21h-6.16l-4.82-6.3L5.25 21H2.19l7.15-8.17L2.25 3h6.32l4.36 5.76zm-1.07 16.14h1.7L7.6 4.77H5.78z" />
    </svg>
  ),
};

const Footer = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);

  const {
    siteFacebookPage,
    siteInstagramPage,
    siteTwitterHandle,
    siteLinkdinPage,
  } = config;

  const siteTwitterPage = twitterPageURL(siteTwitterHandle);

  const socialLinks = [
    siteLinkdinPage && {
      href: siteLinkdinPage,
      title: t('Footer.goToLinkdin'),
      icon: SOCIAL_GLYPHS.linkedin,
      key: 'linkedin',
    },
    siteFacebookPage && {
      href: siteFacebookPage,
      title: t('Footer.goToFacebook'),
      icon: SOCIAL_GLYPHS.facebook,
      key: 'facebook',
    },
    siteInstagramPage && {
      href: siteInstagramPage,
      title: t('Footer.goToInstagram'),
      icon: SOCIAL_GLYPHS.instagram,
      key: 'instagram',
    },
    siteTwitterPage && {
      href: siteTwitterPage,
      title: t('Footer.goToTwitter'),
      icon: SOCIAL_GLYPHS.twitter,
      key: 'twitter',
    },
  ].filter(Boolean);

  const companyLinks = [
    { key: 'work', label: t('Footer.ourWork'), hash: '#portfolio' },
    { key: 'process', label: t('Footer.howWeWork'), hash: '#stats-and-process' },
    { key: 'why', label: t('Footer.whyGitribe'), hash: '#why-gitribe' },
    { key: 'reviews', label: t('Footer.reviews'), hash: '#reviews' },
  ];

  return (
    <footer className={classes}>
      <div className={css.inner}>
        <div className={css.columns}>
          {/* Brand */}
          <div className={css.brand}>
            <Logo
              format="desktop"
              width={150}
              className={css.sitelogo}
              alt={t('TopbarDesktop.logo')}
            />
            <p className={css.slogan}>{t('Footer.slogan')}</p>

            <nav className={css.social} aria-label="Social media links">
              {socialLinks.map(link => (
                <ExternalLink
                  key={link.key}
                  href={link.href}
                  className={css.socialIcon}
                  title={link.title}
                  aria-label={link.title}
                >
                  {link.icon}
                </ExternalLink>
              ))}
            </nav>
          </div>

          {/* Company */}
          <nav className={css.column} aria-label="Company">
            <h3 className={css.columnHeader}>{t('Footer.companyHeader')}</h3>
            <ul className={css.linkList}>
              {/*
                * Services lives here now rather than in a column of its own.
                * Eleven service links made that column more than twice the
                * height of the two beside it and duplicated the topbar menu;
                * the hub page carries the full list, so one link keeps every
                * service a single hop from every page on the site.
                */}
              <li>
                <NamedLink name="ServicesPage" title={t('Footer.servicesHeader')}>
                  {t('Footer.servicesHeader')}
                </NamedLink>
              </li>
              {companyLinks.map(link => (
                <li key={link.key}>
                  <NamedLink name="LandingPage" title={link.label} to={{ hash: link.hash }}>
                    {link.label}
                  </NamedLink>
                </li>
              ))}
              <li>
                <ExternalLink href="https://blog.gitribe.com">{t('Footer.blog')}</ExternalLink>
              </li>
            </ul>
          </nav>

          {/* Contact */}
          <div className={css.column}>
            <h3 className={css.columnHeader}>{t('Footer.contactUs')}</h3>
            <ul className={css.linkList}>
              <li>
                <ExternalLink href="mailto:contact@gitribe.com">
                  {t('Footer.gitribeEmail')}
                </ExternalLink>
              </li>
              <li>
                <ExternalLink href="tel:+923054000453">{t('Footer.phone')}</ExternalLink>
              </li>
              <li>
                <ExternalLink
                  href={
                    process.env.REACT_APP_SCHEDULE_MEETING_URL ||
                    'https://calendly.com/gitribe/30min'
                  }
                >
                  {t('Footer.scheduleMeeting')}
                </ExternalLink>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={css.bottom}>
          <p className={css.copyright}>
            © {new Date().getFullYear()} gitribe. {t('Footer.rightsReserved')}
          </p>
          <nav className={css.legal} aria-label="Legal links">
            <NamedLink name="TermsOfServicePage">{t('Footer.terms')}</NamedLink>
            <span aria-hidden="true">·</span>
            <NamedLink name="PrivacyPolicyPage">{t('Footer.privacy')}</NamedLink>
          </nav>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(Footer);
